import { Stats } from '@react-three/drei';
import { type RefObject, useEffect, useState } from 'react';

interface PlayerDebugStatsProps {
  parent: RefObject<HTMLElement | null>;
}

const PLAYER_DEBUG_STATS_CLASS_NAME = 'cartwhl-player-debug-stats';

export default function PlayerDebugStats({ parent }: PlayerDebugStatsProps) {
  const visible = usePlayerDebugStatsVisible();

  useEffect(() => {
    if (!visible) {
      return;
    }

    const parentElement = parent.current;
    if (!parentElement) {
      return;
    }

    const applyStatsPosition = () => {
      const statsElement = parentElement.querySelector<HTMLDivElement>(
        `.${PLAYER_DEBUG_STATS_CLASS_NAME}`,
      );
      if (!statsElement) {
        return false;
      }

      statsElement.style.position = 'absolute';
      statsElement.style.top = '20px';
      statsElement.style.left = 'auto';
      statsElement.style.right = '20px';
      statsElement.style.zIndex = '50';
      statsElement.style.pointerEvents = 'auto';
      return true;
    };

    if (applyStatsPosition()) {
      return;
    }

    const observer = new MutationObserver(() => {
      applyStatsPosition();
    });

    observer.observe(parentElement, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [parent, visible]);

  if (!visible) {
    return null;
  }

  return (
    <Stats
      parent={parent as RefObject<HTMLElement>}
      className={PLAYER_DEBUG_STATS_CLASS_NAME}
    />
  );
}

function usePlayerDebugStatsVisible() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.repeat ||
        !event.shiftKey ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.key.toLowerCase() !== 'd' ||
        shouldIgnoreDebugStatsHotkey(event)
      ) {
        return;
      }

      event.preventDefault();
      setVisible((current) => !current);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return visible;
}

function shouldIgnoreDebugStatsHotkey(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null;
  if (!target?.tagName) {
    return false;
  }

  const tagName = target.tagName.toUpperCase();

  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    target.isContentEditable
  );
}
